import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { CatalogSyncDataService } from './catalog/catalogsync-data.service';
import { CatalogGuideComponent } from './catalog/guide/catalog-guide.component';
import { BuyingGuideModel } from './catalog/guide/buying-guide.model';
import { CatalogGuideDataService } from './catalog/guide/catalog-guide-data.service';
import { Transition, UIRouterModule } from '@uirouter/angular';
import { STATE_ORDER_ASSET, STATE_ORDER_CATALOG, STATE_ORDER_CATALOG_DETAIL, STATE_ORDER_CATALOG_OVERVIEW, STATE_ORDER_SEARCH } from '../../shared/app-states';
import { OrderEntryModule } from './entry/order-entry.module';
import { OrderEntryWebshopService } from './entry/webshop/order-entry-webshop.service';
import { OrderWebshopLoaderService } from './entry/webshop/order-webshop-loader.service';
import { OrderEntryPaymentService } from './entry/payment/order-entry-payment.service';
import { OrderEntryWebshopDataService } from './entry/webshop/order-entry-webshop-data.service';
import { OrderCatalogOverviewComponent } from './catalog/overview/order-catalog-overview.component';
import { OrderCatalogDetailComponent } from './catalog/detail/order-catalog-detail.component';
import { OrderEntryInfoService } from './entry/order-info/order-entry-info.service';
import { OrderCatalogService } from './catalog/order-catalog.service';
import { OrderCatalogDataService } from './catalog/order-catalog-data.service';
import { CatalogOverviewService } from './catalog/overview/catalog-overview.service';
import { CatalogOverviewFiltersUtilService } from './catalog/overview/catalog-overview-filters-util.service';
import { OrderCatalogComponent } from './catalog/order-catalog.component';
import { CatalogControlsComponent } from './catalog/overview/catalog-controls.component';
import { CatalogLayoutSelectorFieldComponent } from './catalog/overview/catalog-layout-selector-field.component';
import { CatalogFiltersComponent } from './catalog/overview/catalog-filters.component';
import { CatalogContentComponent } from './catalog/overview/catalog-content.component';
import { CatalogOverviewLayoutBlockComponent } from './catalog/overview/catalog-overview-layout-block.component';
import { CatalogOverviewLayoutTablightComponent } from './catalog/overview/catalog-overview-layout-tablight.component';
import { PunchoutCatalogComponent } from './entry/catalog/punchout-catalog.component';
import { OrderPunchoutModule } from './punchout/punchout.module';
import { PunchoutCatalogService } from './entry/catalog/punchout-catalog.service';
import { CatalogStateService } from './catalog/catalog-state.service';
import { CatalogCategoriesComponent } from './catalog/overview/catalog-categories.component';
import { OrderEntryDataService } from './entry/order-entry-data.service';
import { PriceCartService } from './cart/price-cart.service';
import { PriceCartDataService } from './cart/price-cart-data.service';
import { OrderSharedModule } from './order-shared.module';
import { OrderEntryInfoDataService } from './entry/order-info/order-entry-info-data.service';
import { OrderEntryChargesService } from './entry/charges/order-entry-charges.service';
import { TranslationCategory } from '@app/shared/services/ga-state.service';
import { AssetInfoComponent } from './asset/asset-info.component';
import { AssetDataService } from './asset/asset-data.service';
import { AssetDetailComponent } from './asset/asset-detail.component';
import { OrderSearchComponent } from './search/order-search.component';
import { OrderSearchResultDetailComponent } from './search/order-search-result-detail.component';
import { GaStateDeclaration } from '@type_definitions/datatypes';
import { TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';

export const catalogSearchParams:string = 'layout&pmin&pmax&sort&num&page&terms&cat&guide&filters&instock&fav&type';

const states:GaStateDeclaration[] = [
  {
    name: STATE_ORDER_SEARCH,
    url: '/order/search?query',
    component: OrderSearchComponent,
    data: {
      viewTitle: 'Order Search',
      translate: true,
      modules: [
        TranslationCategory.ORDERENTRY,
        TranslationCategory.CATALOG
      ]
    },
    resolve: [
      {
        token: 'query',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().query
      },
      TRANSLATION_READY_RESOLVER
    ],
    params: {
      query: {
        dynamic: true
      }
    }
  },
  {
    name: STATE_ORDER_CATALOG,
    url: '/catalog?currency&billType&webshop&country&stock&workflowType&debug&defaultSort&bpCompanyCountryId&stockOrderType',
    abstract: true,
    views: {
      content: {
        component: OrderCatalogComponent
      }
    },
    data: {
      translate: true,
      modules: [TranslationCategory.CATALOG]
    },
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'currency',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().currency;
        }
      },
      {
        token: 'billType',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().billType;
        }
      },
      {
        token: 'workflowType',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().workflowType;
        }
      },
      {
        token: 'webshopId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          const webshop:string = trans.params().webshop;
          return webshop && Number(webshop);
        }
      },
      {
        token: 'countryId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          const country:string = trans.params().country;
          return country && Number(country);
        }
      },
      {
        token: 'bpCompanyCountryId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          const bpCCId:string = trans.params().bpCompanyCountryId;
          return bpCCId && Number(bpCCId);
        }
      },
      {
        token: 'stock',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().stock === 'true';
        }
      },
      {
        token: 'layout',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().layout;
        }
      },
      {
        token: 'defaultSort',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().defaultSort;
        }
      },
      {
        token: 'stockOrderType',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().stockOrderType;
        }
      }
    ]
  },
  {
    name: STATE_ORDER_CATALOG_OVERVIEW,
    url: `?${catalogSearchParams}&compat`,
    data: {
      viewTitle: 'Catalog'
    },
    params: {
      page: {
        dynamic: true
      }
    },
    component: OrderCatalogOverviewComponent
  },
  {
    name: STATE_ORDER_CATALOG_DETAIL,
    url: '/detail?product&bundle&compat',
    data: {
      viewTitle: 'Product Details'
    },
    component: OrderCatalogDetailComponent
  },
  {
    name: STATE_ORDER_ASSET,
    url: '/orderentry/asset?order&type&serialisable&readonly',
    component: AssetInfoComponent,
    data: {
      translate: true,
      modules: [TranslationCategory.ORDERENTRY],
      viewTitle: 'Order - Asset Info / Item Status'
    },
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'orderId',
        deps: [Transition],
        resolveFn: (transition:Transition) => Number(transition.params().order) || undefined
      },
      {
        token: 'type',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().type;
        }
      },
      {
        token: 'serialisable',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().serialisable;
        }
      },
      {
        token: 'readonly',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().readonly;
        }
      }
    ]
  }
];

@NgModule({
  declarations: [
    CatalogOverviewLayoutBlockComponent,
    CatalogOverviewLayoutTablightComponent,
    CatalogGuideComponent,
    OrderCatalogComponent,
    OrderCatalogOverviewComponent,
    OrderCatalogDetailComponent,
    CatalogCategoriesComponent,
    CatalogControlsComponent,
    CatalogLayoutSelectorFieldComponent,
    CatalogFiltersComponent,
    CatalogContentComponent,
    PunchoutCatalogComponent,
    AssetInfoComponent,
    AssetDetailComponent,
    OrderSearchComponent,
    OrderSearchResultDetailComponent
  ],
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule,
    OrderSharedModule,
    OrderEntryModule,
    OrderPunchoutModule
  ],
  providers: [
    OrderEntryDataService,
    PunchoutCatalogService,
    CatalogSyncDataService,
    CatalogGuideDataService,
    BuyingGuideModel,
    OrderEntryWebshopService,
    OrderWebshopLoaderService,
    OrderEntryWebshopDataService,
    OrderCatalogService,
    OrderCatalogDataService,
    CatalogOverviewService,
    CatalogOverviewFiltersUtilService,
    PriceCartService,
    PriceCartDataService,
    CatalogStateService,
    OrderEntryInfoDataService,
    OrderEntryInfoService,
    OrderEntryPaymentService,
    OrderEntryChargesService,
    AssetDataService
  ],
  exports: [
    PunchoutCatalogComponent
  ]
})
export class OrderModule { }